'use client';

import { useCallback, useEffect, useEffectEvent, useRef } from 'react';

import {
  attemptRecovery as runAttemptRecovery,
  createListeningRecoveryRefs,
  createRecoveryInterval,
  primeShouldBeListeningTimer,
  resetForInactiveSession,
  resetTimerOnListeningStart,
  runRecoveryIntervalTick,
  trackIsStartingBegin,
} from './project-usability-handsfree-listening-recovery.helpers';

interface UseHandsFreeListeningRecoveryOptions {
  isRecording: boolean;
  isCurrentlyPaused: boolean;
  isSubmitting: boolean;
  isConversationOver: boolean;
  isAiVoiceEnabled: boolean;
  isListening: boolean;
  isStarting: boolean;
  audioLevel: number;
  startListening: () => Promise<void>;
  stopListening: (reset?: boolean) => Promise<void>;
}

/**
 * Aggressive recovery mechanism for HandsFree Voice Mode.
 *
 * Detects when we should be listening but aren't, and automatically recovers.
 * Uses multiple detection methods:
 * 1. Time-based: If stuck for STUCK_AUTO_RECOVER_MS, trigger recovery
 * 2. Audio-level-based: If user is speaking (audioLevel > threshold) but we're not listening, immediate recovery
 *
 * Recovery process:
 * - Force stop listening with reset to clear all worker state
 * - Wait briefly to ensure cleanup completes
 * - Start listening again
 * - Track attempts to prevent infinite loops
 */
export function useHandsFreeListeningRecovery({
  isRecording,
  isCurrentlyPaused,
  isSubmitting,
  isConversationOver,
  isAiVoiceEnabled,
  isListening,
  isStarting,
  audioLevel,
  startListening,
  stopListening,
}: UseHandsFreeListeningRecoveryOptions) {
  const recoveryRefs = useRef(createListeningRecoveryRefs());

  const shouldBeListening =
    isRecording &&
    !isCurrentlyPaused &&
    !isSubmitting &&
    !isConversationOver &&
    isAiVoiceEnabled;

  useEffect(() => {
    primeShouldBeListeningTimer(
      recoveryRefs.current,
      shouldBeListening,
      isListening
    );
  }, [shouldBeListening, isListening]);

  useEffect(() => {
    resetTimerOnListeningStart(
      recoveryRefs.current,
      isListening,
      shouldBeListening
    );
  }, [isListening, shouldBeListening]);

  useEffect(() => {
    trackIsStartingBegin(
      recoveryRefs.current,
      isStarting,
      shouldBeListening,
      isListening
    );
  }, [isStarting, shouldBeListening, isListening]);

  const attemptRecovery = useCallback(
    () =>
      runAttemptRecovery(recoveryRefs.current, {
        isStarting,
        isListening,
        startListening,
        stopListening,
      }),
    [isStarting, isListening, startListening, stopListening]
  );

  // More robust detection: Check if we're in active voice session (simpler than shouldBeListening)
  // This ensures recovery runs even if shouldBeListening has edge cases
  const isInActiveVoiceSession =
    isRecording &&
    !isCurrentlyPaused &&
    !isSubmitting &&
    !isConversationOver &&
    isAiVoiceEnabled;

  const onRecoveryTick = useEffectEvent(() => {
    runRecoveryIntervalTick(recoveryRefs.current, {
      shouldBeListening,
      isInActiveVoiceSession,
      isListening,
      isStarting,
      audioLevel,
      runAttemptRecovery: attemptRecovery,
    });
  });

  const isRecoveryActive = shouldBeListening || isInActiveVoiceSession;

  useEffect(() => {
    if (!isRecoveryActive) {
      resetForInactiveSession(recoveryRefs.current);
      return;
    }

    const id = createRecoveryInterval(() => onRecoveryTick());

    return () => clearInterval(id);
  }, [isRecoveryActive]);
}
